import React, {useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import ReactSelect, {components, OptionProps} from 'react-select';
import TableMetronic from '../../../../app/common/components/table/TableMetronic';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {compteFTP} from '../../Types/service';
import {ReadableSizeHelper} from '../../Helpers/readableSize';
import {useIntl} from 'react-intl';
import {ReclamationReason, ReclamationTypes} from '../../../Ticket/Type/reclamation';
import {useCompteFTPByBuId} from '../../Hooks/HebergementManager';
import {useGetMe} from '../../../../app/hooks/useGetMe';
import {isClientCIS} from '../../../Auth/Utils/CustomerHelper';
import ComingSoon from '../../../../app/common/components/ComingSoon';
import EtatBadge from '../EtatBadge';
import {PageTitle} from '../../../../_metronic/layout/core';

interface FilterOption {
  value: string;
  label: string;
  count: number;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

const WebFtpConsommation = (): JSX.Element => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ReadableSizeHelperInstance = useMemo(() => new ReadableSizeHelper(), []);
  const intl = useIntl();
  const customer = useGetMe();
  const buId: string = searchParams.get('buId') ?? '';
  const {isLoading, data} = useCompteFTPByBuId(buId);
  const [selectedAccounts, setSelectedAccounts] = useState<FilterOption[]>([]);
  const [selectedEtats, setSelectedEtats] = useState<FilterOption[]>([]);

  const accountOptions = useMemo<FilterOption[]>(() => {
    const map = new Map<string, FilterOption>();
    (data ?? []).forEach((c) => {
      const key = c.id.toString();
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }
      map.set(key, {value: key, label: `${c.id} - ${c.uid}`, count: 1});
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const etatOptions = useMemo<FilterOption[]>(() => {
    const map = new Map<string, FilterOption>();
    (data ?? []).forEach((c) => {
      const key = c.active ? '1' : '0';
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }
      const label = c.active
        ? intl.formatMessage({id: 'COMMON.STATUS.ACTIVE'})
        : intl.formatMessage({id: 'COMMON.STATUS.INACTIVE'});
      map.set(key, {value: key, label, count: 1});
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [data, intl]);

  const filteredData = useMemo(() => {
    if (!data) return data;
    if (selectedAccounts.length === 0 && selectedEtats.length === 0) return data;
    const accountValues = new Set(selectedAccounts.map((o) => o.value));
    const etatValues = new Set(selectedEtats.map((o) => o.value));
    return data.filter((c) => {
      const accountOk = accountValues.size === 0 || accountValues.has(c.id.toString());
      const etatOk = etatValues.size === 0 || etatValues.has(c.active ? '1' : '0');
      return accountOk && etatOk;
    });
  }, [data, selectedAccounts, selectedEtats]);

  const CheckboxOption = (props: OptionProps<FilterOption, true>) => (
    <components.Option {...props}>
      <div className="d-flex align-items-start">
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
          className="me-2 mt-1 flex-shrink-0"
        />
        <label className="mb-0 flex-grow-1" style={{wordBreak: 'break-word'}}>
          {props.label}
        </label>
        {props.data.count != null && (
          <span className="ms-2 text-muted flex-shrink-0">({props.data.count})</span>
        )}
      </div>
    </components.Option>
  );

  const renderQuotaCell = (row: compteFTP, fullWidth = false): JSX.Element => {
    const isUnlimited = row.quota_limit === 0;
    let color = '#5da170';
    const completionRate = isUnlimited
      ? 100
      : Math.round((100 * row.quota_usage) / row.quota_limit);
    if (!isUnlimited) {
      if (completionRate >= 75 && completionRate < 90) {
        color = '#0dcaf0';
      }
      if (completionRate >= 90 && completionRate < 99) {
        color = '#ffc107';
      }
      if (completionRate >= 99) {
        color = '#dc3545';
      }
    }
    const rate = Math.min(100, completionRate);
    const usageLabel = `${ReadableSizeHelperInstance.readableSize(
      row.quota_usage * 1000,
      0,
      'decimal'
    )} / ${
      isUnlimited
        ? 'Illimité'
        : ReadableSizeHelperInstance.readableSize(row.quota_limit * 1000, 0, 'decimal')
    }`;

    return (
      <div style={{width: fullWidth ? '100%' : '180px', position: 'relative'}}>
        <div
          style={{
            background: 'lightgray',
            height: '23.45px',
            borderRadius: '0.5em',
            overflow: 'hidden',
            fontSize: '0.95em',
          }}
        >
          <div
            style={{
              width: `${rate}%`,
              height: '100%',
              backgroundColor: color,
              transition: 'width 0.6s ease',
            }}
          />
        </div>
        <span
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.95em',
            fontWeight: 500,
            color: '#fff',
          }}
        >
          {usageLabel}
        </span>
      </div>
    );
  };

  const columns = useMemo<ColumnDef<compteFTP>[]>(() => {
    return [
      {
        header: 'Compte',
        accessorKey: 'uid',
      },
      {
        header: 'Etat',
        accessorKey: 'active',
        cell: (info) => {
          const row = info.row.original;

          return (
            <EtatBadge
              color={row.active ? '#5da170' : '#710080'}
              label={
                row.active
                  ? intl.formatMessage({id: 'COMMON.STATUS.ACTIVE'})
                  : intl.formatMessage({id: 'COMMON.STATUS.INACTIVE'})
              }
            />
          );
        },
      },
      {
        header: 'Stockage utilisé',
        accessorKey: 'quota_usage',
        cell: (info) => renderQuotaCell(info.row.original),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ReadableSizeHelperInstance, intl]);

  const renderMobileCard = (row: compteFTP): JSX.Element => (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <span className="fw-bold" style={{minWidth: 0, wordBreak: 'break-word'}}>
          {row.uid}
        </span>
        <span className="flex-shrink-0">
          <EtatBadge
            color={row.active ? '#5da170' : '#710080'}
            label={
              row.active
                ? intl.formatMessage({id: 'COMMON.STATUS.ACTIVE'})
                : intl.formatMessage({id: 'COMMON.STATUS.INACTIVE'})
            }
          />
        </span>
      </div>
      <div>
        <span className="text-muted fs-8 fw-semibold text-uppercase d-block mb-1">
          Stockage utilisé
        </span>
        {renderQuotaCell(row, true)}
      </div>
    </div>
  );

  if (!isClientCIS(customer)) {
    return <ComingSoon />;
  }

  const filtersToolbar = (
    <>
      <div style={{minWidth: 220, flex: 1, maxWidth: 320}}>
        <ReactSelect<FilterOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={accountOptions}
          value={selectedAccounts}
          onChange={(vals) => setSelectedAccounts(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_ACCOUNTS'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
      <div style={{minWidth: 220, flex: 1, maxWidth: 320}}>
        <ReactSelect<FilterOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={etatOptions}
          value={selectedEtats}
          onChange={(vals) => setSelectedEtats(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_STATUS'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
    </>
  );

  const goToReclamation = () => {
    navigate('/support', {
      state: {
        reclamationData: {
          type: ReclamationTypes.TECHNICAL,
          motif: ReclamationReason.REQUEST_FOR_CONFIGURATION,
          objet: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.OBJECT'}),
        },
      },
    });
  };

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.CLOUD.GROUP_WEB'})}</PageTitle>
      <div className="d-flex justify-content-end mb-3">
        <button className="btn btn-primary btn-sm" onClick={goToReclamation}>
          <i
            className="fa fa-file-circle-exclamation"
            aria-label="Nouveau compte Web/FTP"
            aria-hidden="true"
            title="Nouveau compte Web/FTP"
            style={{padding: '0.35rem'}}
          />{' '}
          Nouveau compte Web/FTP
        </button>
      </div>
      <TableMetronic<compteFTP>
        title="Compte web / FTP et Consommation"
        toolbar={filtersToolbar}
        stackedHeader
        data={filteredData ?? []}
        columns={columns}
        renderMobileCard={renderMobileCard}
        isLoading={isLoading}
        searchBar
        pagination
      />
    </>
  );
};
export default WebFtpConsommation;
